import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ReplaySubject, Subscription } from 'rxjs';

import { LocationService } from '@callrail/looky/util';
import { AppNotice } from '../../models/app-notice.model';
import { CurrentContextService } from '../current-context/current-context.service';

/**
 * This service is injected into an interceptor and is used to
 * update the client when a resrouce has been modified in a way
 * that requires a reload of the current context.
 * The context will automatically reload, running all guards and resolvers.
 * The user will recieve a message suggesting they reload their page,
 * to refresh components that still treat context as static.
 */
@Injectable({
  providedIn: 'root',
})
export class ClientReloadService implements OnDestroy {
  public notification$ = new ReplaySubject<Partial<AppNotice>>(1);
  private _modifiedAt: number = Date.now(); // unix timestamp in UTC
  private timerSubscription: Subscription;

  constructor(
    private router: Router,
    private context: CurrentContextService,
    private locationService: LocationService
  ) {}

  /**
   * Public interface
   */
  public set contextLastModified(at: number) {
    const isMoreRecent = at > this._modifiedAt;
    this._modifiedAt = at;
    if (isMoreRecent) {
      this.updateClient();
    }
  }

  public reload(): void {
    this.locationService.reload();
  }

  public ngOnDestroy() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  /**
   * Private methods
   */
  private updateClient(): void {
    this.reloadAgency();
    this.updateNotification();
  }

  /**
   * Reload the agency in the context and add a matrix param to url
   * THIS WILL RERUN ALL GUARDS AND RESOLVERS
   * This is ran every time an agency is updated
   */
  private reloadAgency(): void {
    this.context.reloadAgency(() => {
      const url = this.router.url.split(/\?|;|%|#/)[0];
      this.router.navigate([url, { updatedAt: this._modifiedAt }], {
        queryParamsHandling: 'merge',
      });
    });
  }

  private updateNotification(): void {
    const alert: Partial<AppNotice> = {
      type: 'info',
      icon: 'update',
      created_at: new Date(this._modifiedAt),
    };
    this.notification$.next(alert);
  }
}
